import React from "react"
import PropTypes from "prop-types"
import { Modal, ModalBody } from "reactstrap"
import CustomButton from "../OnboardingComponents/CustomButton"

const LEVEL_ROWS = [
  { level: "Level 1", range: "0-999 XP" },
  { level: "Level 2", range: "1,000 XP" },
  { level: "Level 3", range: "2,000 XP" },
  { level: "Level 4", range: "3,000 XP" },
  { level: "Level 5+", range: "+1,000 XP per level" },
]

const XP_ACTIVITY_ROWS = [
  {
    activity: "Add a Past or Present Project at signup",
    xp: "+250 XP / project",
  },
  { activity: "Start a new project", xp: "+20 XP" },
  { activity: "Solo Working", xp: "+10 XP / hour" },
  { activity: "Shadow Working", xp: "+20 XP / hour" },
  { activity: "Complete a project", xp: "+2 XP / hour worked" },
  { activity: "Published Review - Editor approved", xp: "+60 XP" },
  {
    activity: "Published Event Photography - Editor approved",
    xp: "+60 XP",
  },
]

const LEVEL_UP_STEPS = [
  "Your profile badge updates to your new level.",
  "You receive upgrades to your collaborator invites and cities.",
  "You'll receive a level-up notification.",
  "Your notification takes you to a city-specific treasure list.",
]

const REWARD_ITEMS = [
  "Will-call tickets",
  "Museum memberships",
  "Album downloads",
  "Gifts from local businesses",
]

/**
 * Informational guide for XP, levels, Arts Genome, and rewards.
 * Opened from the Rewards & XP page header.
 */
const XpLevelGuideModal = ({
  isOpen,
  onClose,
  level = 1,
  xpValue = 0,
  xpMax = 1000,
  progressPercent = 0,
  xpToNextLabel = "",
}) => {
  const safePercent = Math.min(100, Math.max(0, Number(progressPercent) || 0))
  const toNext =
    xpToNextLabel ||
    `${Math.max(0, (Number(xpMax) || 0) - (Number(xpValue) || 0)).toLocaleString()} XP to Next Level`

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--xp-guide"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body xp-guide-modal__body">
        <div className="xp-guide-modal">
          <div className="xp-guide-modal__main">
            <header className="xp-guide-modal__header">
              <h2 className="xp-guide-modal__title">
                XP (Experience Points) &amp; Level Guide
              </h2>
              <p className="xp-guide-modal__subtitle">
                Your activity on Amok Earth builds XP (Experience Points) and
                shapes your Arts Genome.
              </p>
            </header>

            <section
              className="xp-guide-modal__progress"
              aria-label="Your level progress"
            >
              <div className="xp-guide-modal__progress-top">
                <div className="xp-guide-modal__progress-level">
                  <span className="xp-guide-modal__progress-label">
                    Your Level
                  </span>
                  <span className="xp-guide-modal__level-pill">
                    Level {level}
                  </span>
                </div>
                <span className="xp-guide-modal__progress-percent">
                  {safePercent}%
                </span>
              </div>
              <div
                className="xp-guide-modal__progress-bar"
                role="progressbar"
                aria-valuenow={safePercent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Level progress"
              >
                <div
                  className="xp-guide-modal__progress-fill"
                  style={{ width: `${safePercent}%` }}
                />
              </div>
              <div className="xp-guide-modal__progress-meta">
                <span>
                  {Number(xpValue).toLocaleString()} /{" "}
                  {Number(xpMax).toLocaleString()} XP
                </span>
                <span className="xp-guide-modal__progress-next">{toNext}</span>
              </div>
            </section>

            <section className="xp-guide-modal__section">
              <div className="xp-guide-modal__section-intro">
                <h3 className="xp-guide-modal__section-title">
                  How Levels Work
                </h3>
                <p className="xp-guide-modal__section-copy">
                  Your Level reflects your contribution over time. You earn XP
                  (Experience Points) by creating projects, working on them,
                  completing them, and contributing to the community. Every
                  1,000 XP moves you into the next level.
                </p>
              </div>
              <div className="xp-guide-modal__section-body">
                <div
                  className="xp-guide-modal__table"
                  role="table"
                  aria-label="Level XP ranges"
                >
                  <div
                    className="xp-guide-modal__table-row xp-guide-modal__table-row--head"
                    role="row"
                  >
                    <div role="columnheader">Level</div>
                    <div role="columnheader">XP Range</div>
                  </div>
                  {LEVEL_ROWS.map(row => (
                    <div
                      key={row.level}
                      className="xp-guide-modal__table-row"
                      role="row"
                    >
                      <div role="cell">{row.level}</div>
                      <div className="xp-guide-modal__table-value" role="cell">
                        {row.range}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="xp-guide-modal__note">
                  Your profile badge updates automatically when you level up.
                </p>
              </div>
            </section>

            <section className="xp-guide-modal__section">
              <div className="xp-guide-modal__section-intro">
                <h3 className="xp-guide-modal__section-title">
                  How You Earn XP (Experience Points)
                </h3>
                <p className="xp-guide-modal__section-copy">
                  Your work is what moves you forward.
                </p>
              </div>
              <div
                className="xp-guide-modal__table"
                role="table"
                aria-label="XP earning activities"
              >
                <div
                  className="xp-guide-modal__table-row xp-guide-modal__table-row--head"
                  role="row"
                >
                  <div role="columnheader">Activity</div>
                  <div role="columnheader">XP Earned</div>
                </div>
                {XP_ACTIVITY_ROWS.map(row => (
                  <div
                    key={row.activity}
                    className="xp-guide-modal__table-row"
                    role="row"
                  >
                    <div role="cell">{row.activity}</div>
                    <div className="xp-guide-modal__table-value" role="cell">
                      {row.xp}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="xp-guide-modal__section">
              <div className="xp-guide-modal__section-intro">
                <h3 className="xp-guide-modal__section-title">
                  Your Work Also Shapes Your Arts Genome
                </h3>
                <p className="xp-guide-modal__section-copy">
                  Your projects, roles, categories, keywords, and working
                  patterns continuously shape your Arts Genome.
                </p>
              </div>
              <div className="xp-guide-modal__section-body">
                <div className="xp-guide-modal__genome">
                  <div className="xp-guide-modal__genome-card xp-guide-modal__genome-card--spellbook">
                    <span className="xp-guide-modal__genome-label">
                      Spellbooks
                    </span>
                    <p className="xp-guide-modal__genome-value">
                      Music, Film, Visual Arts, Performing Arts, Literary Arts
                    </p>
                  </div>
                  <div className="xp-guide-modal__genome-card xp-guide-modal__genome-card--class">
                    <span className="xp-guide-modal__genome-label">Classes</span>
                    <p className="xp-guide-modal__genome-value">
                      Creator, Organizer, Supporter
                    </p>
                  </div>
                </div>
                <p className="xp-guide-modal__note">
                  Your Arts Genome always totals 100%.
                </p>
              </div>
            </section>

            <hr className="xp-guide-modal__divider" />

            <section className="xp-guide-modal__section">
              <div className="xp-guide-modal__section-intro">
                <h3 className="xp-guide-modal__section-title">
                  What Happens When You Level Up?
                </h3>
                <p className="xp-guide-modal__section-copy">
                Level up. Get some upgrades and encouragement. Keep going!
                </p>
              </div>
              <ol className="xp-guide-modal__steps">
                {LEVEL_UP_STEPS.map((step, index) => (
                  <li key={step} className="xp-guide-modal__step">
                    <span
                      className="xp-guide-modal__step-num"
                      aria-hidden="true"
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="xp-guide-modal__step-text">{step}</span>
                  </li>
                ))}
              </ol>
            </section>

            <hr className="xp-guide-modal__divider" />

            <section className="xp-guide-modal__section xp-guide-modal__section--rewards">
              <div className="xp-guide-modal__section-intro">
                <h3 className="xp-guide-modal__section-title">Treasure</h3>
                <p className="xp-guide-modal__section-copy">
                Items to choose from on the City Treasure List may include:
                </p>
              </div>
              <ul className="xp-guide-modal__bullets">
                {REWARD_ITEMS.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="xp-guide-modal__note">
                After claiming, you&apos;ll
                receive an email with redemption instructions.
              </p>
            </section>
          </div>

          <div className="xp-guide-modal__actions">
            <CustomButton
              variant="danger"
              size="md"
              type="button"
              className="xp-guide-modal__cancel"
              onClick={onClose}
            >
              Cancel
            </CustomButton>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

XpLevelGuideModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  level: PropTypes.number,
  xpValue: PropTypes.number,
  xpMax: PropTypes.number,
  progressPercent: PropTypes.number,
  xpToNextLabel: PropTypes.string,
}

export default XpLevelGuideModal
